import React, { useState } from 'react';
import { Gap, Modal, Text } from '@common/components';
import { useLazyQuery, useMutation, useQuery } from '@common/hooks';
import { useFeatureFlags } from '@common/contexts/feature-flags';
import type { FullValidationErrorFragmentType } from '@common/graphql/fragments';
import { createMessage } from 'works/legacy/lib/message-helper';
import { datetime, todayIso } from '@common/services/datetime';
import type { LoadBalancingWorkerTermType } from '@load-balancing/graphql/queries/load-balancing-worker';
import { LOAD_BALANCING_WORKER_SKILLS_QUERY } from '@load-balancing/graphql/queries/load-balancing-worker-skills';
import { PROJECTED_UNASSIGNED_SHIFT_COUNT_QUERY } from '@load-balancing/graphql/queries/projected-unassigned-shift-count';
import { SAVE_WORKER_PROFILE_CHANGE_MUTATION } from '@load-balancing/graphql/mutations/save-worker-profile-change';
import {
  SKILLS_NOT_LOADED_COPY,
  buildSaveVariables,
  initialFormValues,
  isEndBeforeStart,
  isPast,
  laterChanges,
  skillEditsAwaitingLoad,
  skillsLockedByEndDate,
  type WorkerProfileFormValues,
} from './_schedule-worker-profile-update-form-service';

export function useScheduleWorkerProfileUpdateForm({
  workerId,
  scheduleGroupId,
  terms,
  asOf,
  editingTermId = null,
  schedulingPeriodId = null,
  workerName,
  skillsEnabled = false,
  onSaved,
  onUnassigningSave,
}: {
  workerId: string;
  scheduleGroupId: string;
  terms: readonly LoadBalancingWorkerTermType[];
  asOf: string;
  editingTermId?: string | null;
  schedulingPeriodId?: string | null;
  workerName?: string;
  skillsEnabled?: boolean;
  onSaved: () => void;
  onUnassigningSave?: (effectiveDate: string, endDate: string) => void;
}) {
  const { isSchedulingIncludeExcludeEnabled } = useFeatureFlags();
  const [values, setValues] = useState<WorkerProfileFormValues>(() =>
    initialFormValues({ terms, asOf, editingTermId })
  );
  const [saveAttempted, setSaveAttempted] = useState(false);
  const [serverErrors, setServerErrors] = useState<
    FullValidationErrorFragmentType[]
  >([]);

  const {
    data: skillsData,
    loading: skillsLoading,
    error: skillsError,
  } = useQuery(LOAD_BALANCING_WORKER_SKILLS_QUERY, {
    variables: {
      id: workerId,
      scheduleGroupId,
      on: values.effectiveDate || asOf,
    },
    skip: !skillsEnabled || !workerId || !scheduleGroupId,
  });
  const heldSkills = skillsData?.loadBalancingWorker.skills;
  const loadedSkillUuids =
    skillsLoading || skillsError || !heldSkills
      ? null
      : heldSkills.map((skill) => skill.id);
  const initialEndDate =
    terms.find((term) => term.id === editingTermId)?.effectiveEndDate ?? '';
  const skillsLocked = skillsLockedByEndDate(values.endDate, initialEndDate);
  const skillsUnsettled = skillEditsAwaitingLoad({
    values,
    loaded: loadedSkillUuids,
    skillsEnabled,
    skillsLocked,
  });

  // NOTE: network-only because the count must cover every in-review schedule as of this
  // save, not the schedule on screen or a cached number.
  const [fetchProjectedCount, { loading: countLoading }] = useLazyQuery(
    PROJECTED_UNASSIGNED_SHIFT_COUNT_QUERY,
    { fetchPolicy: 'network-only' }
  );

  const [mutate, { loading: saving }] = useMutation(
    SAVE_WORKER_PROFILE_CHANGE_MUTATION,
    {
      refetchQueries: [
        'LoadBalancingWorker',
        'LoadBalancingWorkerSkills',
        'ScheduleGroupWorkers',
        'ScheduleReview',
      ],
      // NOTE: the client defaults errorPolicy to 'all' (legacy/lib/graph-ql.js), so GraphQL
      // errors resolve here with no worker instead of rejecting.
      onCompleted: (data) => {
        const result = data?.saveWorkerProfileChange;
        const errors = result?.errors ?? [];
        if (errors.length > 0) {
          setServerErrors(errors);
          return;
        }
        if (!result?.worker) {
          createMessage(
            'error',
            'Could not save the profile change. Please try again.'
          );
          return;
        }
        createMessage('success', 'Profile change saved.');
        onSaved();
      },
      onError: () =>
        createMessage(
          'error',
          'Could not save the profile change. Please try again.'
        ),
    }
  );

  function update(patch: Partial<WorkerProfileFormValues>) {
    setValues((prev) => ({ ...prev, ...patch }));
  }

  function persist() {
    mutate({
      variables: buildSaveVariables({
        values,
        workerId,
        scheduleGroupId,
        editingTermId,
        schedulingPeriodId,
        skillsEnabled,
        skillsLocked,
        loadedSkillUuids,
      }),
    });
  }

  function save() {
    setSaveAttempted(true);
    if (!values.effectiveDate || isPast(values.effectiveDate)) return;
    if (isEndBeforeStart(values.effectiveDate, values.endDate)) return;
    if (skillsUnsettled) {
      if (!skillsLoading) createMessage('error', SKILLS_NOT_LOADED_COPY);
      return;
    }

    const replaced =
      !editingTermId && !values.endDate
        ? laterChanges(terms, values.effectiveDate)
        : [];
    if (replaced.length > 0) {
      confirmReplaceFutureChanges(
        values.effectiveDate,
        persistOrConfirmUnassign
      );
      return;
    }

    persistOrConfirmUnassign();
  }

  // NOTE: the backend re-sweeps the saved window on every save of an inactive change, so
  // the warning keys on the live server count alone and never on a client-side guess.
  async function persistOrConfirmUnassign() {
    if (values.accountActive) {
      persist();
      return;
    }

    const affectedCount = await projectedUnassignedCount();
    // NOTE: null means the count query failed, so it falls through to the warning rather
    // than saving silently.
    if (affectedCount === 0) {
      persist();
      return;
    }

    confirmUnassignShifts({
      workerName,
      effectiveDate: values.effectiveDate,
      endDate: values.endDate,
      affectedCount,
      enabled: isSchedulingIncludeExcludeEnabled,
      onConfirm: () => {
        onUnassigningSave?.(values.effectiveDate, values.endDate);
        persist();
      },
    });
  }

  async function projectedUnassignedCount(): Promise<number | null> {
    // NOTE: the error is read off this call's result because the hook's render-level error
    // is a stale closure inside the save flow.
    const result = await fetchProjectedCount({
      variables: {
        id: workerId,
        scheduleGroupId,
        effectiveStartDate: values.effectiveDate,
        effectiveEndDate: values.endDate || null,
      },
    }).catch(() => null);
    if (!result || result.error) return null;

    return (
      result.data?.loadBalancingWorker?.projectedUnassignedShiftCount ?? null
    );
  }

  return {
    values,
    update,
    saveAttempted,
    serverErrors,
    saving: saving || countLoading || (skillsUnsettled && skillsLoading),
    save,
    loadedSkillUuids,
    skillsError,
    skillsLocked,
  };
}

function ConfirmUnassignShiftContent({
  immediate,
  name,
  effectiveDate,
  endDate,
  affectedCount,
  enabled,
}: {
  immediate: boolean;
  name: string;
  effectiveDate: string;
  endDate: string;
  affectedCount: number | null;
  enabled: boolean;
}) {
  const startLabel = immediate
    ? 'today'
    : datetime(effectiveDate).format('MMM D, YYYY');
  const windowLabel = endDate
    ? `${startLabel} through ${datetime(endDate).format('MMM D, YYYY')}`
    : startLabel;
  const outcome = enabled ? 'excluded from scheduling' : 'inactive';
  const periodLabel = enabled ? 'excluded' : 'inactive';
  return (
    <Gap vertical size="sm" align="stretch">
      <Text>
        {immediate && !endDate
          ? `${name} will be ${outcome} starting today. All assigned shifts during this schedule period will be unassigned.`
          : `${name} will be ${outcome} starting ${windowLabel}. Any shifts assigned during the ${periodLabel} period will be unassigned.`}
      </Text>
      {affectedCount !== null && (
        <Text>
          {`Affected shifts: ${affectedCount} assigned ${
            affectedCount === 1 ? 'shift' : 'shifts'
          }`}
        </Text>
      )}
    </Gap>
  );
}

function confirmUnassignShifts({
  workerName,
  effectiveDate,
  endDate,
  affectedCount,
  enabled,
  onConfirm,
}: {
  workerName?: string;
  effectiveDate: string;
  endDate: string;
  affectedCount: number | null;
  enabled: boolean;
  onConfirm: () => void;
}) {
  const immediate = effectiveDate <= todayIso();
  const name = workerName ?? 'This worker';

  Modal.confirm({
    title: immediate
      ? 'All assigned shifts will be unassigned'
      : 'Assigned shifts will be unassigned',
    content: (
      <ConfirmUnassignShiftContent
        immediate={immediate}
        name={name}
        effectiveDate={effectiveDate}
        endDate={endDate}
        affectedCount={affectedCount}
        enabled={enabled}
      />
    ),
    okText: 'Save and Unassign',
    cancelText: 'Cancel',
    danger: true,
    onOk: onConfirm,
  });
}

function confirmReplaceFutureChanges(
  effectiveDate: string,
  onConfirm: () => void
) {
  Modal.confirm({
    title: 'Replace future scheduled changes?',
    content: (
      <Gap vertical size="sm" align="stretch">
        <Text>
          {`This change has no end date and will become the worker's profile from ${datetime(
            effectiveDate
          ).format(
            'MMM D, YYYY'
          )} onward. Any scheduled changes after that date will be removed.`}
        </Text>
        <Text>
          To keep future scheduled changes, add an end date to make this a
          temporary change.
        </Text>
      </Gap>
    ),
    okText: 'Confirm',
    onOk: onConfirm,
  });
}
